/* ==========================================================================
   Twisted Tailor — Theme JavaScript
   Vanilla JS, no dependencies
   ========================================================================== */
(function () {
  'use strict';

  /* ========================================================================
     Utilities
     ======================================================================== */

  /**
   * Debounce — delays execution until after `wait` ms of inactivity.
   */
  function debounce(fn, wait) {
    var timer;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  /**
   * Throttle — limits execution to once every `limit` ms.
   */
  function throttle(fn, limit) {
    var waiting = false;
    return function () {
      if (waiting) return;
      fn.apply(this, arguments);
      waiting = true;
      setTimeout(function () { waiting = false; }, limit);
    };
  }

  /**
   * Format cents into a currency string using Shopify's money format.
   * Supports {{ amount }}, {{ amount_no_decimals }}, {{ amount_with_comma_separator }}.
   */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    var fmt = format || window.theme && window.theme.moneyFormat || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var dollarsWithDot = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    return fmt
      .replace('{{amount_with_comma_separator}}', parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1])
      .replace('{{amount_no_decimals_with_comma_separator}}', dollarsWithDot)
      .replace('{{amount_no_decimals}}', parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount}}', dollars + '.' + parts[1]);
  }

  /**
   * Fetch JSON helper with error handling.
   */
  function fetchJSON(url, options) {
    return fetch(url, options).then(function (res) {
      if (!res.ok) throw new Error(res.statusText);
      return res.json();
    });
  }

  /**
   * Trap focus inside an element (for modals / drawers).
   */
  function trapFocus(el) {
    var focusable = el.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    first.focus();

    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    el.addEventListener('keydown', handler);
    el._releaseFocusTrap = function () { el.removeEventListener('keydown', handler); };
  }

  function releaseFocusTrap(el) {
    if (el._releaseFocusTrap) el._releaseFocusTrap();
  }

  /* ========================================================================
     Sticky Header with scroll-direction detection
     ======================================================================== */
  var Header = (function () {
    var header, lastScroll, threshold;

    function init() {
      header = document.querySelector('.tt-header');
      if (!header) return;
      lastScroll = 0;
      threshold = 60;
      window.addEventListener('scroll', throttle(onScroll, 100), { passive: true });
    }

    function onScroll() {
      var current = window.pageYOffset;
      if (current <= threshold) {
        header.classList.remove('tt-header--hidden');
        lastScroll = current;
        return;
      }
      if (current > lastScroll) {
        header.classList.add('tt-header--hidden');
      } else {
        header.classList.remove('tt-header--hidden');
      }
      lastScroll = current;
    }

    return { init: init };
  })();

  /* ========================================================================
     Mobile Menu
     ======================================================================== */
  var MobileMenu = (function () {
    function init() {
      // Handled via delegation in the global click handler
    }

    function toggle() {
      var menu = document.querySelector('[data-mobile-menu]');
      if (!menu) return;
      var isOpen = menu.getAttribute('aria-hidden') === 'false';
      menu.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
      document.body.classList.toggle('tt-menu-open', !isOpen);
      var toggle = document.querySelector('[data-action="toggle-menu"]');
      if (toggle) toggle.setAttribute('aria-expanded', String(!isOpen));
    }

    return { init: init, toggle: toggle };
  })();

  /* ========================================================================
     Cart Drawer
     ======================================================================== */
  var CartDrawer = (function () {
    var drawer, overlay;

    function init() {
      drawer = document.querySelector('.tt-cart-drawer');
      overlay = document.querySelector('.tt-cart-drawer__overlay');
    }

    function open() {
      if (!drawer) return;
      drawer.classList.add('tt-cart-drawer--open');
      if (overlay) overlay.classList.add('tt-cart-drawer__overlay--visible');
      document.body.style.overflow = 'hidden';
      trapFocus(drawer);
    }

    function close() {
      if (!drawer) return;
      drawer.classList.remove('tt-cart-drawer--open');
      if (overlay) overlay.classList.remove('tt-cart-drawer__overlay--visible');
      document.body.style.overflow = '';
      releaseFocusTrap(drawer);
    }

    /**
     * Add an item to the cart via AJAX, then refresh the drawer contents.
     */
    function addToCart(formData) {
      return fetchJSON('/cart/add.js', {
        method: 'POST',
        body: formData
      }).then(function () {
        return refreshCart();
      }).then(function () {
        open();
      });
    }

    /**
     * Update a line item quantity.
     */
    function updateQuantity(line, quantity) {
      return fetchJSON('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      }).then(function () {
        return refreshCart();
      });
    }

    /**
     * Remove a line item (quantity = 0).
     */
    function removeItem(line) {
      return updateQuantity(line, 0);
    }

    /**
     * Fetch the latest cart state and re-render the drawer.
     */
    function refreshCart() {
      return fetchJSON('/cart.js').then(function (cart) {
        renderCart(cart);
        updateCartCount(cart.item_count);
        return cart;
      });
    }

    /**
     * Render cart drawer contents from cart object.
     */
    function renderCart(cart) {
      var itemsEl = drawer && drawer.querySelector('.tt-cart-drawer__items');
      var footerEl = drawer && drawer.querySelector('.tt-cart-drawer__footer');
      if (!itemsEl) return;

      if (cart.item_count === 0) {
        itemsEl.innerHTML = '<p class="tt-cart-drawer__empty">Your cart is empty.</p>';
        if (footerEl) footerEl.style.display = 'none';
        return;
      }
      if (footerEl) footerEl.style.display = '';

      var html = '';
      cart.items.forEach(function (item, i) {
        html +=
          '<div class="tt-cart-drawer__item">' +
            '<div class="tt-cart-drawer__item-image">' +
              '<img src="' + getSizedImageUrl(item.image, '160x') + '" alt="' + escapeHtml(item.title) + '" loading="lazy">' +
            '</div>' +
            '<div class="tt-cart-drawer__item-details">' +
              '<p class="tt-cart-drawer__item-title">' + escapeHtml(item.product_title) + '</p>' +
              (item.variant_title ? '<p class="tt-cart-drawer__item-variant">' + escapeHtml(item.variant_title) + '</p>' : '') +
              '<p class="tt-cart-drawer__item-price">' + formatMoney(item.final_line_price) + '</p>' +
              '<div class="tt-qty">' +
                '<button class="tt-qty__btn" data-action="cart-qty" data-line="' + (i + 1) + '" data-dir="down" aria-label="Decrease quantity">&minus;</button>' +
                '<input class="tt-qty__input" type="number" value="' + item.quantity + '" min="0" data-action="cart-qty-input" data-line="' + (i + 1) + '" aria-label="Quantity">' +
                '<button class="tt-qty__btn" data-action="cart-qty" data-line="' + (i + 1) + '" data-dir="up" aria-label="Increase quantity">&plus;</button>' +
              '</div>' +
              '<button class="tt-btn--link" data-action="cart-remove" data-line="' + (i + 1) + '">Remove</button>' +
            '</div>' +
          '</div>';
      });
      itemsEl.innerHTML = html;

      /* Update subtotal and shipping bar */
      var subtotalEl = drawer.querySelector('[data-cart-subtotal]');
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

      updateShippingBar(cart.total_price);
    }

    /**
     * Update the free shipping progress bar.
     */
    function updateShippingBar(totalCents) {
      var threshold = window.theme && window.theme.freeShippingThreshold || 10000; // default 100.00
      var fill = drawer && drawer.querySelector('.tt-cart-drawer__shipping-fill');
      var msg = drawer && drawer.querySelector('.tt-cart-drawer__shipping-msg');
      if (!fill) return;
      var pct = Math.min((totalCents / threshold) * 100, 100);
      fill.style.width = pct + '%';
      if (msg) {
        if (totalCents >= threshold) {
          msg.textContent = 'You qualify for free shipping!';
        } else {
          var remaining = formatMoney(threshold - totalCents);
          msg.textContent = 'Spend ' + remaining + ' more for free shipping';
        }
      }
    }

    /**
     * Update all cart count badges on the page.
     */
    function updateCartCount(count) {
      var badges = document.querySelectorAll('[data-cart-count]');
      badges.forEach(function (el) {
        el.textContent = count;
        el.style.display = count > 0 ? '' : 'none';
      });
    }

    return {
      init: init,
      open: open,
      close: close,
      addToCart: addToCart,
      updateQuantity: updateQuantity,
      removeItem: removeItem,
      refreshCart: refreshCart
    };
  })();

  /* ========================================================================
     Product Variant Selector
     ======================================================================== */
  var VariantSelector = (function () {
    var productData;

    function init() {
      var el = document.querySelector('[data-product-json]');
      if (!el) return;
      try { productData = JSON.parse(el.textContent); } catch (e) { return; }
    }

    /**
     * When an option button or select changes, find the matching variant.
     */
    function onOptionChange(container) {
      if (!productData) return;
      var options = [];
      container.querySelectorAll('[data-option-index]').forEach(function (group) {
        var selected = group.querySelector('.tt-option--selected, input:checked, select');
        if (selected) {
          var val = selected.value || selected.getAttribute('data-value');
          options.push(val);
        }
      });

      var variant = productData.variants.find(function (v) {
        return v.options.every(function (opt, i) { return opt === options[i]; });
      });

      if (variant) onVariantFound(variant, container);
    }

    function onVariantFound(variant, container) {
      /* Update price */
      var priceEl = container.querySelector('[data-product-price]');
      if (priceEl) {
        priceEl.innerHTML = variant.compare_at_price && variant.compare_at_price > variant.price
          ? '<s>' + formatMoney(variant.compare_at_price) + '</s> <ins>' + formatMoney(variant.price) + '</ins>'
          : formatMoney(variant.price);
      }

      /* Update URL without reload */
      if (history.replaceState) {
        var url = window.location.pathname + '?variant=' + variant.id;
        history.replaceState(null, '', url);
      }

      /* Update add-to-cart button availability */
      var addBtn = container.querySelector('[data-action="add-to-cart"]');
      if (addBtn) {
        if (variant.available) {
          addBtn.removeAttribute('disabled');
          addBtn.textContent = 'Add to Bag';
        } else {
          addBtn.setAttribute('disabled', 'disabled');
          addBtn.textContent = 'Sold Out';
        }
      }

      /* Update hidden variant ID input */
      var idInput = container.querySelector('input[name="id"]');
      if (idInput) idInput.value = variant.id;

      /* Update product image if variant has one */
      if (variant.featured_image) {
        var mainImg = document.querySelector('[data-product-main-image]');
        if (mainImg) {
          mainImg.src = getSizedImageUrl(variant.featured_image.src, '800x');
          mainImg.alt = variant.featured_image.alt || '';
        }
      }

      /* Highlight active option button */
      container.querySelectorAll('[data-option-index]').forEach(function (group, i) {
        group.querySelectorAll('[data-value]').forEach(function (btn) {
          btn.classList.toggle('tt-option--selected', btn.getAttribute('data-value') === variant.options[i]);
        });
      });
    }

    return { init: init, onOptionChange: onOptionChange };
  })();

  /* ========================================================================
     Accordion
     ======================================================================== */
  var Accordion = (function () {
    function toggle(trigger) {
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;
      var expanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(!expanded));
      panel.setAttribute('aria-hidden', String(expanded));
    }

    return { toggle: toggle };
  })();

  /* ========================================================================
     Announcement Bar
     ======================================================================== */
  var AnnouncementBar = (function () {
    var STORAGE_KEY = 'tt_announcement_dismissed';

    function init() {
      var bar = document.querySelector('.tt-announcement');
      if (!bar) return;
      var id = bar.getAttribute('data-announcement-id') || 'default';
      if (localStorage.getItem(STORAGE_KEY) === id) {
        bar.hidden = true;
      }
    }

    function dismiss() {
      var bar = document.querySelector('.tt-announcement');
      if (!bar) return;
      var id = bar.getAttribute('data-announcement-id') || 'default';
      bar.hidden = true;
      localStorage.setItem(STORAGE_KEY, id);
    }

    return { init: init, dismiss: dismiss };
  })();

  /* ========================================================================
     Lazy Loading (IntersectionObserver)
     ======================================================================== */
  var LazyLoad = (function () {
    var observer;

    function init() {
      if (!('IntersectionObserver' in window)) {
        /* Fallback: load all immediately */
        document.querySelectorAll('[data-src]').forEach(function (el) { load(el); });
        return;
      }
      observer = new IntersectionObserver(onIntersect, {
        rootMargin: '200px 0px'
      });
      document.querySelectorAll('[data-src]').forEach(function (el) {
        observer.observe(el);
      });
    }

    function onIntersect(entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          load(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }

    function load(el) {
      if (el.dataset.src) {
        el.src = el.dataset.src;
        el.removeAttribute('data-src');
      }
      if (el.dataset.srcset) {
        el.srcset = el.dataset.srcset;
        el.removeAttribute('data-srcset');
      }
    }

    return { init: init };
  })();

  /* ========================================================================
     Scroll Animations
     ======================================================================== */
  var ScrollAnim = (function () {
    var observer;

    function init() {
      if (!('IntersectionObserver' in window)) {
        document.querySelectorAll('.tt-fade-in').forEach(function (el) {
          el.classList.add('tt-fade-in--visible');
        });
        return;
      }
      observer = new IntersectionObserver(onIntersect, {
        threshold: 0.1,
        rootMargin: '0px 0px -40px 0px'
      });
      document.querySelectorAll('.tt-fade-in').forEach(function (el) {
        observer.observe(el);
      });
    }

    function onIntersect(entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('tt-fade-in--visible');
          observer.unobserve(entry.target);
        }
      });
    }

    return { init: init };
  })();

  /* ========================================================================
     Modal
     ======================================================================== */
  var Modal = (function () {
    var activeModal, previousFocus;

    function open(id) {
      activeModal = document.getElementById(id);
      if (!activeModal) return;
      previousFocus = document.activeElement;
      activeModal.classList.add('tt-modal--open');
      document.body.style.overflow = 'hidden';
      trapFocus(activeModal);

      /* Close on Escape */
      document.addEventListener('keydown', onEscape);
    }

    function close() {
      if (!activeModal) return;
      activeModal.classList.remove('tt-modal--open');
      document.body.style.overflow = '';
      releaseFocusTrap(activeModal);
      document.removeEventListener('keydown', onEscape);
      if (previousFocus) previousFocus.focus();
      activeModal = null;
    }

    function onEscape(e) {
      if (e.key === 'Escape') close();
    }

    return { open: open, close: close };
  })();

  /* ========================================================================
     Quantity Selector
     ======================================================================== */
  var QuantitySelector = (function () {
    function change(btn) {
      var wrapper = btn.closest('.tt-qty');
      if (!wrapper) return;
      var input = wrapper.querySelector('.tt-qty__input');
      if (!input) return;
      var current = parseInt(input.value, 10) || 1;
      var min = parseInt(input.min, 10) || 1;
      var dir = btn.getAttribute('data-dir');
      var next = dir === 'up' ? current + 1 : Math.max(min, current - 1);
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    return { change: change };
  })();

  /* ========================================================================
     Predictive Search
     ======================================================================== */
  var PredictiveSearch = (function () {
    var resultsContainer, input;

    function init() {
      input = document.querySelector('[data-predictive-search-input]');
      resultsContainer = document.querySelector('[data-predictive-search-results]');
      if (!input || !resultsContainer) return;

      input.addEventListener('input', debounce(function () {
        var q = input.value.trim();
        if (q.length < 2) {
          hideResults();
          return;
        }
        search(q);
      }, 300));

      /* Close results when clicking outside */
      document.addEventListener('click', function (e) {
        if (!e.target.closest('[data-predictive-search]')) hideResults();
      });
    }

    function search(query) {
      var url = '/search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=6';
      fetchJSON(url).then(function (data) {
        var products = data.resources && data.resources.results && data.resources.results.products || [];
        render(products);
      }).catch(function () {
        hideResults();
      });
    }

    function render(products) {
      if (!products.length) {
        resultsContainer.innerHTML = '<p style="padding:1rem;">No results found.</p>';
        resultsContainer.classList.add('tt-search-results--open');
        return;
      }
      var html = '';
      products.forEach(function (p) {
        var img = p.image ? getSizedImageUrl(p.image, '80x') : '';
        html +=
          '<a href="' + p.url + '" class="tt-search-results__item">' +
            (img ? '<img src="' + img + '" alt="" loading="lazy" style="width:3rem;height:3rem;object-fit:cover;">' : '<span></span>') +
            '<span>' + escapeHtml(p.title) + '</span>' +
          '</a>';
      });
      resultsContainer.innerHTML = html;
      resultsContainer.classList.add('tt-search-results--open');
    }

    function hideResults() {
      if (resultsContainer) {
        resultsContainer.classList.remove('tt-search-results--open');
        resultsContainer.innerHTML = '';
      }
    }

    return { init: init };
  })();

  /* ========================================================================
     Helpers
     ======================================================================== */

  /**
   * Get a sized Shopify image URL.
   */
  function getSizedImageUrl(src, size) {
    if (!src) return '';
    if (typeof src === 'object') src = src.src || '';
    return src.replace(/(\.[^.]+)$/, '_' + size + '$1');
  }

  /**
   * Basic HTML escaping.
   */
  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  /* ========================================================================
     Global Event Delegation
     ======================================================================== */
  document.body.addEventListener('click', function (e) {
    var target = e.target.closest('[data-action]');
    if (!target) return;
    var action = target.getAttribute('data-action');

    switch (action) {
      /* Cart */
      case 'toggle-cart':
        e.preventDefault();
        CartDrawer.open();
        break;

      case 'close-cart':
        e.preventDefault();
        CartDrawer.close();
        break;

      case 'add-to-cart':
        e.preventDefault();
        var form = target.closest('form');
        if (form) CartDrawer.addToCart(new FormData(form));
        break;

      case 'cart-qty':
        e.preventDefault();
        var line = parseInt(target.getAttribute('data-line'), 10);
        var dir = target.getAttribute('data-dir');
        var qtyInput = target.closest('.tt-qty').querySelector('.tt-qty__input');
        var current = parseInt(qtyInput.value, 10) || 1;
        var next = dir === 'up' ? current + 1 : current - 1;
        if (next < 1) next = 0; // 0 = remove
        CartDrawer.updateQuantity(line, next);
        break;

      case 'cart-remove':
        e.preventDefault();
        CartDrawer.removeItem(parseInt(target.getAttribute('data-line'), 10));
        break;

      /* Mobile menu */
      case 'toggle-menu':
        e.preventDefault();
        MobileMenu.toggle();
        break;

      /* Accordion */
      case 'toggle-accordion':
        e.preventDefault();
        Accordion.toggle(target);
        break;

      /* Announcement dismiss */
      case 'dismiss-announcement':
        e.preventDefault();
        AnnouncementBar.dismiss();
        break;

      /* Quantity selector (non-cart) */
      case 'qty-change':
        e.preventDefault();
        QuantitySelector.change(target);
        break;

      /* Modal */
      case 'open-modal':
        e.preventDefault();
        Modal.open(target.getAttribute('data-target'));
        break;

      case 'close-modal':
        e.preventDefault();
        Modal.close();
        break;

      /* Variant option click (clickybox style) */
      case 'select-option':
        e.preventDefault();
        var group = target.closest('[data-option-index]');
        if (group) {
          group.querySelectorAll('[data-action="select-option"]').forEach(function (btn) {
            btn.classList.remove('tt-option--selected');
          });
          target.classList.add('tt-option--selected');
          var container = target.closest('[data-product-form]');
          if (container) VariantSelector.onOptionChange(container);
        }
        break;
    }
  });

  /* Handle cart drawer overlay click */
  document.body.addEventListener('click', function (e) {
    if (e.target.classList.contains('tt-cart-drawer__overlay')) {
      CartDrawer.close();
    }
    if (e.target.classList.contains('tt-modal__overlay')) {
      Modal.close();
    }
  });

  /* Handle <select> based variant changes */
  document.body.addEventListener('change', function (e) {
    if (e.target.matches('[data-option-index] select')) {
      var container = e.target.closest('[data-product-form]');
      if (container) VariantSelector.onOptionChange(container);
    }
  });

  /* Handle Escape key for drawer */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      var drawer = document.querySelector('.tt-cart-drawer--open');
      if (drawer) CartDrawer.close();
    }
  });

  /* ========================================================================
     Init on DOMContentLoaded
     ======================================================================== */
  document.addEventListener('DOMContentLoaded', function () {
    Header.init();
    MobileMenu.init();
    CartDrawer.init();
    VariantSelector.init();
    AnnouncementBar.init();
    LazyLoad.init();
    ScrollAnim.init();
    PredictiveSearch.init();

    /* On initial load, fetch cart state to populate count badges */
    CartDrawer.refreshCart();
  });

})();
